import React, { useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Header } from "../../components/landing/header";
import { Footer } from "../../components/landing/footer";
import "../landing/Landing.css";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | iClipCoupons";
    window.scrollTo(0, 0);

    // Update meta description for SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    const originalDescription = metaDescription?.getAttribute('content');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Privacy Policy for iClipCoupons digital coupon platform. Learn how we collect, use, and protect your data across our web dashboard and mobile application.'
      );
    }

    // Cleanup: restore original description when component unmounts
    return () => {
      if (metaDescription && originalDescription) {
        metaDescription.setAttribute('content', originalDescription);
      }
    };
  }, []);

  const lastUpdated = "February 26, 2026";
  const companyName = "iClipCoupons";
  const contactEmail = "support@iclipcoupons.com";
  const contactAddress = "242 Main Street, Ste #102, Beacon, New York 12508";
  const contactPhone = "(845) 326-7279";

  return (
    <div>
      <Header />

      <div className="section privacy-policy-section">
        <div className="container">
          <div className="privacy-policy-content">
            {/* Page Header */}
            <div className="privacy-header text-center">
              <div className="title-section">
                <h3>Legal</h3>
                <h2>Privacy Policy</h2>
              </div>
              <p className="last-updated">Last Updated: {lastUpdated}</p>
            </div>

            {/* Introduction */}
            <section className="policy-section">
              <h2>1. Introduction</h2>
              <p>
                Welcome to {companyName}. We operate a comprehensive digital coupon management platform that consists of:
              </p>
              <ul className="policy-list">
                <li>
                  <strong>Web Dashboard:</strong> A business-facing application that enables merchants, retailers, and manufacturers to create, manage, and distribute QR-code-based digital coupons.
                </li>
                <li>
                  <strong>Mobile Application:</strong> A consumer-facing application that allows end users to discover, save, and redeem digital coupons at participating retailers.
                </li>
              </ul>
              <p>
                This Privacy Policy describes how {companyName} ("we," "us," or "our") collects, uses, discloses, and protects information when you use our web platform, mobile applications, and related services (collectively, the "Services").
              </p>
              <p>
                We are committed to protecting your privacy and ensuring transparency about our data practices. By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="policy-section">
              <h2>2. Information We Collect</h2>

              <h3>A. Information Collected from Business Users (Web Dashboard)</h3>
              <p>When you register for and use our web-based coupon creation platform, we collect:</p>
              <ul className="policy-list">
                <li>
                  <strong>Account Information:</strong> Your name, email address, phone number, and password used to create and secure your account.
                </li>
                <li>
                  <strong>Business Information:</strong> Company name, business address, business type, tax identification numbers (where applicable), and company logo.
                </li>
                <li>
                  <strong>Payment Information:</strong> Billing address and payment method details. Note: All payment processing is handled securely by Stripe, our third-party payment processor. We do not store complete credit card numbers, CVV codes, or other sensitive payment credentials on our servers.
                </li>
                <li>
                  <strong>Coupon Data:</strong> Information you provide when creating coupons, including product descriptions, discount amounts, redemption terms, validity periods, and any restrictions or conditions.
                </li>
                <li>
                  <strong>Generated Content:</strong> QR codes and GS1 DataBar Expanded Stacked codes, along with associated coupon graphics created through our platform.
                </li>
                <li>
                  <strong>Usage Analytics:</strong> Information about how you interact with our dashboard, including pages visited, features used, coupon performance metrics, and redemption statistics.
                </li>
                <li>
                  <strong>Company Prefixes:</strong> GS1 company prefixes and related manufacturer identification data used for barcode generation.
                </li>
              </ul>

              <h3>B. Information Collected from End Users (Mobile Application)</h3>
              <p>When consumers use our mobile application to redeem coupons, we may collect:</p>
              <ul className="policy-list">
                <li>
                  <strong>Basic Profile Information:</strong> Name, email address, or phone number if required for account creation or coupon redemption verification.
                </li>
                <li>
                  <strong>Device Information:</strong> Device type, operating system version, unique device identifiers (such as Advertising ID or IDFA), device model, and mobile network information.
                </li>
                <li>
                  <strong>Coupon Interaction Data:</strong> Records of which coupons you view, save, and redeem, including timestamps and redemption locations.
                </li>
                <li>
                  <strong>Location Data:</strong> With your explicit consent, we may collect precise or approximate location data to help you find nearby participating retailers or to verify redemption eligibility. You may disable location services at any time through your device settings.
                </li>
                <li>
                  <strong>App Usage Data:</strong> Information about how you navigate the application, including session duration, feature interactions, and in-app preferences.
                </li>
                <li>
                  <strong>Camera Access:</strong> When you use our QR code scanner feature, the app requires camera access. We do not store images or video from your camera beyond what is necessary to process the QR code.
                </li>
              </ul>

              <h3>C. Automatically Collected Information</h3>
              <p>For all users, we automatically collect certain information when you access our Services:</p>
              <ul className="policy-list">
                <li>
                  <strong>Log Data:</strong> IP address, browser type and version, time zone setting, browser plug-in types, operating system, and platform.
                </li>
                <li>
                  <strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to enhance your experience, analyze usage patterns, and deliver personalized content.
                </li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="policy-section">
              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>

              <h3>Service Delivery and Operations</h3>
              <ul className="policy-list">
                <li>To create and manage your account and authenticate your identity</li>
                <li>To generate, store, and distribute digital coupons and associated barcodes/QR codes</li>
                <li>To process and validate coupon redemptions in real-time</li>
                <li>To prevent fraudulent activities, including duplicate redemptions, unauthorized usage, and coupon abuse</li>
                <li>To process subscription payments and manage billing cycles</li>
              </ul>

              <h3>Platform Improvement</h3>
              <ul className="policy-list">
                <li>To analyze usage patterns and improve our platform's features, functionality, and user experience</li>
                <li>To develop new products, services, and features based on user needs</li>
                <li>To conduct research and analytics to optimize coupon performance for merchants</li>
              </ul>

              <h3>Communication</h3>
              <ul className="policy-list">
                <li>To send transactional emails, including account confirmations, password resets, and redemption notifications</li>
                <li>To provide customer support and respond to your inquiries</li>
                <li>To send service-related announcements, such as maintenance notifications and policy updates</li>
                <li>With your consent, to send promotional communications about new features or partner offers</li>
              </ul>

              <h3>Legal and Security</h3>
              <ul className="policy-list">
                <li>To comply with legal obligations, including tax reporting and regulatory requirements</li>
                <li>To enforce our Terms of Service and protect against legal liability</li>
                <li>To detect, prevent, and address technical issues, security breaches, and fraud</li>
              </ul>

              <h3>Lawful Bases for Processing (GDPR)</h3>
              <p>
                For users located in the European Economic Area (EEA), United Kingdom, or other jurisdictions requiring a lawful basis for processing, we process personal information under the following bases:
              </p>
              <ul className="policy-list">
                <li>
                  <strong>Performance of a Contract:</strong> To provide and maintain our Services, manage accounts, process transactions, and fulfill our obligations to you.
                </li>
                <li>
                  <strong>Legitimate Interests:</strong> To prevent fraud, ensure platform security, improve functionality, conduct analytics, and maintain the integrity of the coupon ecosystem, provided such interests are not overridden by your data protection rights.
                </li>
                <li>
                  <strong>Consent:</strong> Where required, including for location services, certain marketing communications, and optional features. You may withdraw consent at any time.
                </li>
                <li>
                  <strong>Legal Obligation:</strong> To comply with applicable laws, regulations, tax requirements, and lawful government requests.
                </li>
              </ul>
            </section>

            {/* Payment Information */}
            <section className="policy-section">
              <h2>4. Payment Information and Security</h2>
              <p>
                {companyName} partners with Stripe, a leading payment processing provider, to handle all payment transactions securely. Our payment practices include:
              </p>
              <ul className="policy-list">
                <li>
                  <strong>No Storage of Sensitive Payment Data:</strong> We do not store complete credit card numbers, debit card numbers, CVV/CVC codes, or bank account details on our servers. All sensitive payment information is processed and stored by Stripe in accordance with PCI DSS (Payment Card Industry Data Security Standard) Level 1 compliance.
                </li>
                <li>
                  <strong>Tokenization:</strong> When you provide payment information, Stripe converts it into a secure token that we use for billing purposes. This token cannot be used to retrieve your actual card details.
                </li>
                <li>
                  <strong>Subscription Management:</strong> We maintain records of subscription plans, billing cycles, payment history, and invoice data to manage your account.
                </li>
                <li>
                  <strong>Secure Transmission:</strong> All payment data is transmitted using TLS (Transport Layer Security) encryption.
                </li>
              </ul>
              <p>
                For more information about Stripe's privacy practices, please visit: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>
              </p>
            </section>

            {/* Data Retention */}
            <section className="policy-section">
              <h2>5. Data Retention</h2>
              <p>
                We retain your information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Specific retention practices include:
              </p>
              <ul className="policy-list">
                <li>
                  <strong>Account Data:</strong> We retain your account information for as long as your account is active. If you request account deletion, we will remove your personal data within 30 days, except where retention is required for legal or legitimate business purposes.
                </li>
                <li>
                  <strong>Coupon Records:</strong> Coupon creation data and redemption records are retained for a minimum of 7 years for audit, compliance, and analytics purposes. Aggregated, anonymized data may be retained indefinitely.
                </li>
                <li>
                  <strong>Transaction Records:</strong> Payment and subscription records are retained for 7 years to comply with tax and financial reporting requirements.
                </li>
                <li>
                  <strong>Redemption Logs:</strong> Individual coupon redemption logs are retained for fraud prevention, dispute resolution, and to maintain the integrity of the coupon ecosystem.
                </li>
                <li>
                  <strong>Communication Records:</strong> Customer support correspondence is retained for 3 years to ensure service quality and resolve any disputes.
                </li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="policy-section">
              <h2>6. Data Security</h2>
              <p>
                We implement comprehensive security measures designed to protect your information from unauthorized access, alteration, disclosure, or destruction:
              </p>
              <ul className="policy-list">
                <li>
                  <strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted using TLS 1.2 or higher. Sensitive data at rest is encrypted using AES-256 encryption.
                </li>
                <li>
                  <strong>Secure Authentication:</strong> We implement secure authentication mechanisms including password hashing, session management, and support for multi-factor authentication.
                </li>
                <li>
                  <strong>Access Controls:</strong> We maintain strict access controls to ensure that only authorized personnel can access personal data, and only to the extent necessary to perform their job functions.
                </li>
                <li>
                  <strong>Role-Based Permissions:</strong> Our platform supports role-based access control, allowing business users to manage team member permissions and limit access to sensitive data.
                </li>
                <li>
                  <strong>Regular Security Assessments:</strong> We conduct regular security audits, vulnerability assessments, and penetration testing to identify and address potential security risks.
                </li>
                <li>
                  <strong>Secure Infrastructure:</strong> Our services are hosted on secure cloud infrastructure with industry-standard physical and network security controls.
                </li>
                <li>
                  <strong>Incident Response:</strong> We maintain an incident response plan to quickly address any security breaches and notify affected users as required by applicable law.
                </li>
              </ul>
              <p>
                While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security, but we commit to implementing commercially reasonable safeguards.
              </p>
            </section>

            {/* User Rights */}
            <section className="policy-section">
              <h2>7. Your Rights and Choices</h2>
              <p>
                Depending on your location, you may have certain rights regarding your personal information. We respect these rights and provide mechanisms for you to exercise them:
              </p>
              <ul className="policy-list">
                <li>
                  <strong>Access:</strong> You have the right to request a copy of the personal information we hold about you. You can access much of this information directly through your account dashboard.
                </li>
                <li>
                  <strong>Correction:</strong> You have the right to request that we correct inaccurate or incomplete personal information. You can update most account information directly through your profile settings.
                </li>
                <li>
                  <strong>Deletion:</strong> You have the right to request deletion of your personal information, subject to certain exceptions (such as legal retention requirements or ongoing fraud prevention needs).
                </li>
                <li>
                  <strong>Data Portability:</strong> You have the right to request your data in a structured, commonly used, machine-readable format.
                </li>
                <li>
                  <strong>Withdraw Consent:</strong> Where we rely on consent to process your information, you have the right to withdraw that consent at any time.
                </li>
                <li>
                  <strong>Opt-Out of Marketing:</strong> You can opt out of receiving promotional communications by clicking the unsubscribe link in any marketing email or by contacting us directly.
                </li>
                <li>
                  <strong>Cookie Preferences:</strong> You can manage cookie preferences through your browser settings or through any cookie preference tools we provide on our website.
                </li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at <a href={`mailto:${contactEmail}`}>{contactEmail}</a>. We will respond to your request within 30 days. We may require verification of your identity before processing certain requests.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="policy-section">
              <h2>8. Children's Privacy</h2>
              <p>
                Our Services are not intended for children under the age of 13 (or under 16 in certain jurisdictions). We do not knowingly collect personal information from children under these ages.
              </p>
              <p>
                For Business Users: You must be at least 18 years old to create a business account and use our coupon creation services.
              </p>
              <p>
                For Mobile App Users: Our mobile application is intended for users who are at least 13 years of age. Users between 13 and 18 should review this Privacy Policy with a parent or guardian.
              </p>
              <p>
                If we become aware that we have collected personal information from a child under the applicable minimum age without parental consent, we will take steps to delete that information promptly. If you believe we may have collected information from a child, please contact us immediately at <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
              </p>
            </section>

            {/* International Data Transfers */}
            <section className="policy-section">
              <h2>9. International Data Transfers</h2>
              <p>
                {companyName} is based in the United States. If you access our Services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States or other countries where our service providers operate.
              </p>
              <p>
                These countries may have data protection laws that are different from the laws of your country. By using our Services, you consent to the transfer of your information to the United States and other jurisdictions as described in this Privacy Policy.
              </p>
              <p>
                We implement appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy, regardless of where it is processed.
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="policy-section">
              <h2>10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make changes:
              </p>
              <ul className="policy-list">
                <li>We will update the "Last Updated" date at the top of this Privacy Policy.</li>
                <li>For significant changes, we will provide prominent notice, such as an email notification or a banner on our website or application.</li>
                <li>We encourage you to periodically review this Privacy Policy to stay informed about how we protect your information.</li>
              </ul>
              <p>
                Your continued use of our Services after any changes to this Privacy Policy constitutes your acceptance of the updated policy.
              </p>
            </section>

            {/* California Privacy Rights */}
            <section className="policy-section">
              <h2>11. California Privacy Rights</h2>
              <p>
                If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), including:
              </p>
              <ul className="policy-list">
                <li>The right to know what personal information we collect, use, disclose, and sell</li>
                <li>The right to request deletion of your personal information</li>
                <li>The right to opt-out of the sale or sharing of your personal information</li>
                <li>The right to non-discrimination for exercising your privacy rights</li>
                <li>The right to correct inaccurate personal information</li>
                <li>The right to limit use and disclosure of sensitive personal information</li>
              </ul>
              <p>
                {companyName} does not sell personal information to third parties. We also do not share personal information for cross-context behavioral advertising as defined under the California Privacy Rights Act (CPRA). To exercise your California privacy rights, please contact us using the information provided below.
              </p>
            </section>

            {/* Contact Information */}
            <section className="policy-section">
              <h2>12. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="contact-details">
                <p><strong>{companyName}</strong></p>
                <p><strong>Address:</strong> {contactAddress}</p>
                <p><strong>Email:</strong> <a href={`mailto:${contactEmail}`}>{contactEmail}</a></p>
                <p><strong>Phone:</strong> {contactPhone}</p>
              </div>
              <p>
                We are committed to resolving any privacy concerns and will respond to your inquiry within a reasonable timeframe.
              </p>
            </section>

            {/* Acknowledgment */}
            <section className="policy-section acknowledgment">
              <p>
                By using {companyName} Services, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your information as described herein.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
